import React, { useState, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  Alert,
  AlertIcon,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  Flex,
  Button,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { formatDate } from "../../utils/dateUtils";
import { Api } from "../../Api";
import Pagination from "../shared/Pagination";
import {
  getAllOrganizations,
  getAllUsers,
  getAdminSessions,
} from "../../services/adminService";

const ITEMS_PER_PAGE = 25;

const SessionManagement = () => {
  const [filterDeviceId, setFilterDeviceId] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();
  const toastHelper = createToast(toast);

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;

  const fetchSessions = useCallback(() => {
    const params = {
      skip,
      limit: ITEMS_PER_PAGE,
    };
    if (filterDeviceId) params.device_id = filterDeviceId;
    if (filterActive !== "") params.is_active = filterActive;
    if (filterOrgId) params.organization_id = filterOrgId;
    if (filterUserId) params.user_id = filterUserId;
    return getAdminSessions(params);
  }, [filterDeviceId, filterActive, filterOrgId, filterUserId, skip]);

  const fetchOrganizations = useCallback(() => getAllOrganizations(), []);
  const fetchUsers = useCallback(() => getAllUsers(filterOrgId || null), [filterOrgId]);

  const {
    data: sessionsData,
    loading,
    error,
    refetch: refetchSessions,
  } = useAdminData(fetchSessions, [
    filterDeviceId,
    filterActive,
    filterOrgId,
    filterUserId,
    skip,
  ]);

  const { data: organizationsData } = useAdminData(fetchOrganizations, []);
  const { data: usersData } = useAdminData(fetchUsers, [filterOrgId]);

  const sessions = sessionsData?.sessions || [];
  const totalCount = sessionsData?.total_count || 0;
  const activeCount = sessionsData?.active_count || 0;
  const organizations = organizationsData?.organizations || [];
  const users = usersData?.users || [];

  const handleFilterDeviceId = (value) => {
    setFilterDeviceId(value);
    setCurrentPage(1);
  };

  const handleFilterActive = (value) => {
    setFilterActive(value);
    setCurrentPage(1);
  };

  const handleFilterOrg = (value) => {
    setFilterOrgId(value);
    setFilterUserId("");
    setCurrentPage(1);
  };

  const handleFilterUser = (value) => {
    setFilterUserId(value);
    setCurrentPage(1);
  };

  const handleDeactivateSession = async (session) => {
    if (!window.confirm(`Are you sure you want to deactivate session #${session.id} for device ${session.device_id}?`)) return;
    try {
      await Api.put(`/admin/sessions/${session.id}/deactivate`);
      toastHelper.success("Session deactivated successfully");
      refetchSessions();
    } catch (err) {
      const message =
        err.response?.status === 400
          ? "Session is already inactive"
          : err.response?.data?.detail || "Failed to deactivate session";
      toastHelper.error(message);
    }
  };

  const truncateSessionId = (sessionId) => {
    if (!sessionId) return "—";
    return sessionId.substring(0, 16);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error?.message || "Failed to load sessions"}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* Filters */}
      <Flex gap={4} wrap="wrap" align="flex-end">
        <FormControl maxW="260px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Organization
          </FormLabel>
          <Select
            size="sm"
            value={filterOrgId}
            onChange={(e) => handleFilterOrg(e.target.value)}
            placeholder="All organizations"
            borderRadius="md"
            bg="white"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="320px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            User
          </FormLabel>
          <Select
            size="sm"
            value={filterUserId}
            onChange={(e) => handleFilterUser(e.target.value)}
            placeholder="All users"
            borderRadius="md"
            bg="white"
          >
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.email}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="280px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Device ID
          </FormLabel>
          <Input
            size="sm"
            value={filterDeviceId}
            onChange={(e) => handleFilterDeviceId(e.target.value)}
            placeholder="e.g., 10:20:BA:46:63:A0"
            fontFamily="mono"
            borderRadius="md"
            bg="white"
          />
        </FormControl>
        <FormControl maxW="180px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Status
          </FormLabel>
          <Select
            size="sm"
            value={filterActive}
            onChange={(e) => handleFilterActive(e.target.value)}
            placeholder="All"
            borderRadius="md"
            bg="white"
          >
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </Select>
        </FormControl>
      </Flex>

      {/* Table */}
      {sessions.length === 0 ? (
        <Box
          py={16} textAlign="center" bg="gray.50" borderRadius="lg"
          borderWidth="1px" borderColor="gray.200" borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No sessions found</Text>
        </Box>
      ) : (
        <>
          <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                  {["#", "Session ID", "User ID", "Device ID", "Status", "Expired", "Created", "Expires", ""].map((h) => (
                    <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {sessions.map((session, idx) => (
                  <Tr
                    key={session.id}
                    borderBottomWidth={idx < sessions.length - 1 ? "1px" : "0"}
                    borderBottomColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.1s"
                  >
                    <Td py={3} px={4}>
                      <Text fontSize="xs" color="gray.400" fontFamily="mono">{session.id}</Text>
                    </Td>
                    <Td py={3} px={4} fontFamily="mono" fontSize="xs">
                      {session.session_id ? (
                        <Tooltip label={session.session_id} placement="top" hasArrow>
                          <Text cursor="default" color="gray.600">{truncateSessionId(session.session_id)}…</Text>
                        </Tooltip>
                      ) : (
                        <Text color="gray.300">—</Text>
                      )}
                    </Td>
                    <Td py={3} px={4}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.600" fontFamily="mono">
                          {session.user_id ?? "—"}
                        </Text>
                        {session.user_email && (
                          <Text fontSize="xs" color="gray.400" noOfLines={1}>
                            {session.user_email}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td py={3} px={4} fontFamily="mono" fontSize="xs">
                      <Text color="gray.700">{session.device_id}</Text>
                    </Td>
                    <Td py={3} px={4}>
                      {session.is_active ? (
                        <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2} fontSize="xs">Active</Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2} fontSize="xs">Inactive</Badge>
                      )}
                    </Td>
                    <Td py={3} px={4}>
                      {session.is_expired ? (
                        <Badge colorScheme="red" variant="subtle" borderRadius="full" px={2} fontSize="xs">Expired</Badge>
                      ) : (
                        <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2} fontSize="xs">Valid</Badge>
                      )}
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="xs" color="gray.400">{formatDate(session.created_at)}</Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="xs" color="gray.400">{formatDate(session.expires_at)}</Text>
                    </Td>
                    <Td py={3} px={4} textAlign="right">
                      {session.is_active && (
                        <Button
                          size="xs" colorScheme="red" variant="ghost" borderRadius="md"
                          onClick={() => handleDeactivateSession(session)}
                        >
                          Deactivate
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex justify="space-between" align="center">
            <Text fontSize="xs" color="gray.400">
              Showing {skip + 1}–{Math.min(skip + ITEMS_PER_PAGE, totalCount)} of {totalCount} sessions · {activeCount} active
            </Text>
            <Pagination
              currentPage={currentPage}
              totalItems={totalCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              isDisabled={loading}
            />
          </Flex>
        </>
      )}
    </VStack>
  );
};

export default SessionManagement;
